import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Grid, MenuItem, TextField, Typography, CardContent, Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import LoaderCircular from 'ui-component/LoaderCircular';
import { fetchReportDataForSkanReporting } from '../../redux/reporting/reportingActions';
import { getDynamicColumns } from '../../utils/dynamicColumns';
import '../Dashboard/styleSearch.scss';
const columns = [
  // { field: 'id', headerName: 'ID', width: 90 },
  {
    field: 'campaign_name',
    headerName: 'Campaign Name',
    flex: 2,
    minWidth: 200,
    renderCell: (params) => <div className="campaign_name_settings">{params.value}</div>
  },
  {
    field: 'publisher_app_name',
    headerName: 'Publisher App Name',
    width: 180,
    renderCell: (params) => {
      const value = params.value?.trim();
      const isUnknown = !value || value.toLowerCase() === 'unknown';
      const displayValue = isUnknown ? '-' : value;
      return (
        <div
          className="campaign_name_settings"
          style={{
            textAlign: isUnknown ? 'center' : 'left', // center only for '-'
            width: '100%'
          }}
        >
          {displayValue}
        </div>
      );
    }
  },
  {
    field: 'app_bundle',
    headerName: 'App Bundle',
    flex: 1,
    minWidth: 100,
    hideable: false,
    renderCell: (params) => <div className="campaign_name_settings">{params.value}</div>
  },
  {
    field: 'country',
    headerName: 'Country',
    flex: 0.8,
    minWidth: 100,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'os_name',
    headerName: 'OS',
    flex: 0.8,
    minWidth: 80,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'clicks',
    headerName: 'Clicks',
    valueFormatter: (params) => splitDigits(params.value),
    flex: 1,
    minWidth: 100,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'impressions',
    headerName: 'Impressions',
    valueFormatter: (params) => splitDigits(params.value),
    flex: 1,
    minWidth: 120,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'media_cost',
    headerName: 'Media Cost',
    valueFormatter: (params) => '$ ' + roundToTwoDecimal(params.value),
    flex: 1,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'installs',
    headerName: 'MMP Installs',
    flex: 1,
    minWidth: 120,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'skan_installs',
    headerName: 'SKAN Installs',
    sort: 'desc',
    flex: 1,
    minWidth: 120,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'ctr',
    headerName: 'CTR',
    valueFormatter: (params) => roundToTwoDecimalPercent(params.value),
    flex: 0.8,
    minWidth: 80,
    align: 'center',
    headerAlign: 'center'
  },
  // {
  //   field: 'ecpe',
  //   headerName: 'Total eCPE',
  //   valueFormatter: (params) => roundToTwoDecimal(params.value),
  //   align: 'center',
  //   headerAlign: 'center',
  //   width: 100
  //   // flex:1
  // },
  {
    field: 'ecpi',
    headerName: 'Total eCPI ($)',
    valueFormatter: (params) => roundToTwoDecimal(params.value),
    flex: 1,
    minWidth: 100,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'ecpi_local',
    headerName: 'Total eCPI (¥)',
    valueFormatter: (params) => roundYen(params.value),
    flex: 1,
    minWidth: 100,
    align: 'center',
    headerAlign: 'center'
  },
  // {
  //   field: 'mmp_ecpe',
  //   headerName: 'MMP eCPE',
  //   valueFormatter: (params) => roundToTwoDecimal(params.value),
  //   align: 'center',
  //   headerAlign: 'center',
  //   width: 100
  //   // flex:1
  // },
  {
    field: 'mmp_ecpi',
    headerName: 'MMP eCPI ($)',
    valueFormatter: (params) => roundToTwoDecimal(params.value),
    align: 'center',
    headerAlign: 'center',
    flex: 1,
    minWidth: 100
  },
  {
    field: 'mmp_ecpi_local',
    headerName: 'MMP eCPI (¥)',
    valueFormatter: (params) => roundYen(params.value),
    align: 'center',
    headerAlign: 'center',
    flex: 1,
    minWidth: 100
  },
  // {
  //   field: 'skan_ecpe',
  //   headerName: 'SKAN eCPE',
  //   valueFormatter: (params) => roundToTwoDecimal(params.value),
  //   align: 'center',
  //   headerAlign: 'center',
  //   width: 100
  //   // flex:1
  // },
  {
    field: 'skan_ecpi',
    headerName: 'SKAN eCPI ($)',
    valueFormatter: (params) => roundToTwoDecimal(params.value),
    align: 'center',
    headerAlign: 'center',
    flex: 1,
    minWidth: 100
  },
  {
    field: 'skan_ecpi_local',
    headerName: 'SKAN eCPI (¥)',
    valueFormatter: (params) => roundYen(params.value),
    align: 'center',
    headerAlign: 'center',
    flex: 1,
    minWidth: 100
  },
  {
    field: 'ecpc',
    headerName: 'eCPC ($)',
    valueFormatter: (params) => roundToTwoDecimal(params.value),
    align: 'center',
    headerAlign: 'center',
    flex: 0.8,
    minWidth: 80
  },
  {
    field: 'ecpc_local',
    headerName: 'eCPC (¥)',
    valueFormatter: (params) => roundYen(params.value),
    align: 'center',
    headerAlign: 'center',
    flex: 0.8,
    minWidth: 80
  },
  {
    field: 'ecpm',
    headerName: 'eCPM ($)',
    valueFormatter: (params) => roundToTwoDecimal(params.value),
    flex: 0.8,
    minWidth: 80,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'ecpm_local',
    headerName: 'eCPM (¥)',
    valueFormatter: (params) => roundYen(params.value),
    flex: 0.8,
    minWidth: 80,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'skan_events',
    headerName: 'SKAN Events',
    flex: 1,
    minWidth: 120,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'mmp_events',
    headerName: 'MMP Events',
    flex: 1,
    minWidth: 120,
    align: 'center',
    headerAlign: 'center'
  },
  {
    field: 'media_cost_local_currency',
    headerName: 'Media Cost (¥)',
    valueFormatter: (params) => '¥ ' + roundToTwoDecimal(params.value),
    flex: 1,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center'
  }
];
function splitDigits(num) {
  return Number(num).toLocaleString();
}
function roundToTwoDecimal(value) {
  if (value == '-999') return '-';
  // return Math.round((value + Number.EPSILON) * 100) / 100;
  return (Math.round((value + Number.EPSILON) * 10000) / 10000).toFixed(4);
}
function roundToTwoDecimalPercent(value) {
  if (value == '-999') return '-';
  // const roundedValue = Math.round((value * 100 + Number.EPSILON) * 100) / 100; // Multiply by 100 to convert to percentage
  // return `${roundedValue}%`; // Append '%' sign
  const roundedValue = Math.round((value * 100 + Number.EPSILON) * 10000) / 10000;
  return `${roundedValue.toFixed(4)}%`;
}
const ReportDataSkanView = ({
  fetchDataForReportingDatatable,
  reportingData,
  fetchReportDataForSkanReporting,
  loading,
  error,
  timeZone,
  isDollar
}) => {
  useEffect(() => {
    //fetchDataForReportingDatatable();
    //console.log(reportingData);
    // fetchReportData()
  }, [reportingData]);
  // console.log("loading==>",loading)
  const getRowHeight = (params) => {
    // console.log(params)
    const column2Value = params.model.campaign_name;
    // console.log(column2Value)
    if (column2Value.length > 110) {
      return 150; // Return the desired row height in pixels
    } else if (column2Value.length > 100) {
      return 130; // Return the desired row height in pixels
    } else {
      return 60;
    }
  };

  const columnVisibilityModel = React.useMemo(() => {
    if (timeZone == 'JST' && isDollar == true) {
      return {
        media_cost_local_currency: false,
        media_cost: true,
        ecpc_local: false,
        ecpc: true,
        ecpm_local: false,
        ecpm: true,
        ecpi_local: false,
        ecpi: true,
        mmp_ecpi_local: false,
        skan_ecpi_local: false
      };
    } else if (timeZone == 'JST' && isDollar == false) {
      return {
        media_cost_local_currency: true,
        media_cost: false,
        ecpc_local: true,
        ecpc: false,
        ecpm_local: true,
        ecpm: false,
        ecpi_local: true,
        ecpi: false,
        mmp_ecpi_local: false,
        skan_ecpi_local: false
      };
    } else {
      return {
        media_cost_local_currency: false,
        media_cost: true,
        ecpc_local: false,
        ecpc: true,
        ecpm_local: false,
        ecpm: true,
        ecpi_local: false,
        ecpi: true,
        mmp_ecpi_local: false,
        skan_ecpi_local: false
      };
    }
  }, [timeZone, isDollar]);

  // Get dynamic columns based on available data
  const dynamicColumns = useMemo(() => {
    return getDynamicColumns(columns, reportingData);
  }, [reportingData]);
  return (
    <>
      {loading ? (
        <MainCard>
          <Grid container spacing={gridSpacing}>
            <Grid item lg={12} md={12} sm={12} xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <Typography variant="h4">Top 500 Publisher Apps Report</Typography>
            </Grid>

            <Grid item xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <div style={{ height: '400px', width: '100%' }}>
                {' '}
                <LoaderCircular show={true} />
              </div>
            </Grid>
          </Grid>
        </MainCard>
      ) : reportingData == 0 ? (
        <MainCard>
          <Grid container spacing={gridSpacing}>
            <Grid item lg={12} md={12} sm={12} xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <Typography variant="h4">Top 500 Publisher Apps Report</Typography>
            </Grid>

            <Grid item xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <Typography style={{ textAlign: 'center' }}>
                {/* Please click <b>Apply Filter</b> to view data */}
                {error.length != 0 ? (
                  'No Data Found'
                ) : (
                  <span>
                    Please click <b>Apply Filter</b> to view data
                  </span>
                )}
              </Typography>
            </Grid>
          </Grid>
        </MainCard>
      ) : (
        <MainCard>
          <Grid container spacing={gridSpacing}>
            <Grid item lg={12} md={12} sm={12} xs={12} alignContent="center" sx={{ textAlign: 'center' }}>
              <Typography variant="h4">Top 500 Publisher Apps Report</Typography>
            </Grid>

            <Grid item xs={12}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item xs={12}>
                  <Box>
                    <div style={{ minHeight: '400px', width: '100%' }}>
                      <style>
                        {`
                          .custom-datagrid {
                            width: 100% !important;
                          }
                          .custom-datagrid .MuiDataGrid-main {
                            width: 100% !important;
                          }
                          .custom-datagrid .MuiDataGrid-virtualScroller {
                            width: 100% !important;
                          }
                        `}
                      </style>
                      {loading ? (
                        <CircularProgress
                          style={{
                            position: 'relative',
                            top: '50%',
                            left: '50%'
                          }}
                        />
                      ) : (
                        <DataGrid
                          rows={reportingData}
                          columns={dynamicColumns}
                          columnVisibilityModel={columnVisibilityModel}
                          initialState={{
                            pagination: {
                              paginationModel: {
                                pageSize: 10
                              }
                            },
                            sorting: {
                              sortModel: [{ field: 'media_cost', sort: 'desc' }] // Default sort on 'age' column
                            }
                          }}
                          components={{
                            LoadingOverlay: CircularProgress
                          }}
                          loading={loading}
                          pageSizeOptions={[10, 20, 30]}
                          slots={{ toolbar: GridToolbar }}
                          disableRowSelectionOnClick
                          disableColumnSelector
                          disableDensitySelector
                          // sortModel={[
                          //   {
                          //     field: "installs",
                          //     sort: "desc", // Set 'desc' for descending, 'asc' for ascending
                          //   },
                          // ]}
                          autoHeight
                          // getRowHeight={getRowHeight}
                          getRowHeight={() => 'auto'}
                          className="custom-datagrid"
                        />
                      )}
                    </div>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </MainCard>
      )}
    </>
  );
};
const mapStateToProps = (state) => {
  return {
    reportingData: state.reportingReducer.reportingData,
    loading: state.reportingReducer.bundleReportLoading,
    error: state.reportingReducer.error,
    timeZone: state.advertiserReducer.timeZone,
    isDollar: state.campaignReducer.isDollar
  };
};
const mapDispatchToProps = {
  fetchReportDataForSkanReporting: fetchReportDataForSkanReporting
};
export default connect(mapStateToProps, mapDispatchToProps)(ReportDataSkanView);
